import { Typography } from "@mui/material";
import { useCallClient } from "components/remoteAccess/callClient";
import {
  CallClientId,
  CallClientOptions,
  CallProps,
  CallRef,
  PeerData,
  PointerData,
} from "components/remoteAccess/helpers";
import styles from "components/remoteAccess/RemoteCall.module.css";
import { DataConnection, MediaConnection } from "peerjs";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useAppSelector } from "store";

export const RemoteViewCamera = forwardRef<CallRef, CallProps>(
  function RemoteViewCamera(props, ref) {
    const { onEnd } = props;
    const { waitingFor } = useAppSelector((s) => s.remoteAccess);

    const videoRef = useRef<HTMLVideoElement>(null);
    const captureCanvasRef = useRef<HTMLCanvasElement>(null);
    const [callActive, setCallActive] = useState<boolean>(false);
    const [pointer, setPointer] = useState<{ x: number; y: number }>();
    const [dataConnection, setDataConnection] = useState<DataConnection>();

    useImperativeHandle(ref, () => ({
      capture() {
        if (!videoRef.current || !captureCanvasRef.current) return;
        if (dataConnection) {
          dataConnection.send(JSON.stringify({ type: "info", data: "screenCapture" }));
        }

        let canvas = captureCanvasRef.current;
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        let context = canvas.getContext("2d");
        context?.drawImage(
          videoRef.current,
          0,
          0,
          videoRef.current.videoWidth,
          videoRef.current.videoHeight,
        );
        let data = canvas.toDataURL();
        return data;
      },
    }));

    const onCallStarted = useCallback((peerStream: MediaStream, _: MediaConnection) => {
      setCallActive(true);

      if (!videoRef.current) return;

      videoRef.current.srcObject = peerStream;
      videoRef.current.onloadedmetadata = () => {
        if (!videoRef.current) return;
        videoRef.current.play();
      };
    }, []);

    const onCallEnded = useCallback((_: MediaConnection) => {
      setCallActive(false);
      console.log("TaskRemoteCall: onCallEnded");
      if (!videoRef.current) return;
      videoRef.current.srcObject = null;
    }, []);

    const onDataReceived = useCallback(
      (data: unknown) => {
        const dataObj: PeerData = JSON.parse(data as string);

        if (dataObj.type === "pointer") {
          if (!videoRef.current) return;

          const { x: xp, y: yp } = dataObj.data as PointerData;
          let width = videoRef.current.clientWidth;
          let height = videoRef.current.clientHeight;

          setPointer({
            x: xp * width,
            y: yp * height,
          });
        } else if (dataObj.type === "info") {
          const commandData = dataObj.data as string;

          if (commandData === "endCall") {
            onEnd();
          }
        }
      },
      [onEnd],
    );

    useCallClient(CallClientId, {
      debug: "RemoteViewCamera",
      clientOptions: CallClientOptions,
      setCallActive,
      onCallStreamReceived: onCallStarted,
      onCallEnded,
      onDataStarted: (data) => setDataConnection(data),
      onDataEnded: () => setDataConnection(undefined),
      onDataReceived,
    });

    const onVideoClicked = useCallback(
      (e: React.MouseEvent<HTMLVideoElement, globalThis.MouseEvent>) => {
        if (!videoRef.current) return;

        let x = e.nativeEvent.offsetX;
        let y = e.nativeEvent.offsetY;
        let width = videoRef.current.clientWidth;
        let height = videoRef.current.clientHeight;

        let px = x / width;
        let py = y / height;
        if (dataConnection) {
          dataConnection.send(JSON.stringify({ type: "pointer", data: { x: px, y: py } }));
        }
        setPointer({ x, y });
      },
      [dataConnection],
    );

    return (
      <>
        {pointer && (
          <div
            className={styles.callPointer}
            style={{
              transform: `translate(${pointer.x}px, ${pointer.y}px) translate(-50%, -50%)`,
            }}
          />
        )}
        <video ref={videoRef} width="100%" autoPlay onClick={onVideoClicked} />
        <canvas ref={captureCanvasRef} style={{ display: "none" }} />
        {!callActive && (
          <div className={styles.noConnectionOverlay}>
            {waitingFor && (
              <div className={styles.noConnectionWaitingText}>
                <Typography variant="h5">
                  <FormattedMessage id="remoteAccess.waitingForConnectionFrom" />
                </Typography>
                <Typography variant="h5" style={{ alignSelf: "center" }}>
                  {waitingFor}
                </Typography>
              </div>
            )}
            {/* Waiting for call message plus end call/cancel button*/}
          </div>
        )}
        <canvas style={{ display: "none" }} />
      </>
    );
  },
);
